import type { PropType } from "vue";
import type { ModelId } from "./Reinforced.Typings.CustomTypes";

/**
 * Returns a Vue PropType for a ModelId property.
 *
 * Automatically handles both plain types and already-wrapped ModelId types:
 * - `propTypeForModelId<'Member'>()` → `PropType<ModelId<'Member'>>`
 * - `propTypeForModelId<ModelId<'Member'>>()` → `PropType<ModelId<'Member'>>` (prevents double-wrapping)
 *
 * Use this when defining Vue component props that expect a ModelId.
 *
 * @example
 * ```typescript
 * // In a Vue component
 * const props = defineProps({
 *   memberId: propTypeForModelId<'Member'>(),
 * });
 * ```
 */
export function propTypeForModelId<T>(): PropType<ModelId<UnwrapModelId<T>>> {
	// Cast through 'unknown' because ModelId<T> is a branded string type.
	// At runtime it's just a string, but TypeScript sees the phantom __modelType property
	// and thinks String constructor is incompatible with the branded type.
	return String as unknown as PropType<ModelId<UnwrapModelId<T>>>;
}

/**
 * Unwraps ModelId<T> to T, or returns T if it's not a ModelId.
 *
 * @example
 * ```typescript
 * type Example1 = UnwrapModelId<ModelId<'Member'>>; // Result: 'Member'
 * type Example2 = UnwrapModelId<'Member'>;          // Result: 'Member'
 * type Example3 = UnwrapModelId<string>;            // Result: string
 * ```
 */
type UnwrapModelId<T> = T extends ModelId<infer U> ? U : T;
